// Display cache for web Home and Updates. A summary is not permission to decide.
// Request order is captured at dispatch. A later login cannot join the
// previous session's read. The bell uses the server count, not cached rows.

const feeds = new Map();
const lists = new Map();
const details = new Map();
const tombstones = new Map();
const inflight = new Map();
let clock = 0;
let session = 1;

function tick() {
  clock += 1;
  return clock;
}

export function currentReviewSession() {
  return session;
}

export function resetReviewSession() {
  session += 1;
  inflight.clear();
}

function scopeKey(userId) {
  return `${String(userId || '')}|${session}`;
}

function stonesFor(userId) {
  const key = scopeKey(userId);
  let found = tombstones.get(key);
  if (!found) {
    found = new Map();
    tombstones.set(key, found);
  }
  return found;
}

export function isStaleDecision(body) {
  return !!body && typeof body === 'object' && body.stale === true;
}

export function isConfirmedDecision(body) {
  if (!body || typeof body !== 'object') return false;
  if (body.stale === true) return false;
  if (typeof body.error === 'string' && body.error.length > 0) return false;
  return body.success === true;
}

export function reviewPresentation(body, updateId) {
  if (!body || typeof body !== 'object') return { show: false, decide: false };
  if (body.id && body.id !== updateId) return { show: false, decide: false };
  const kind = String(body.review_kind || '');
  const status = body.status;
  if (kind === 'name_hygiene') return { show: true, decide: true };
  if (!kind && (body.old || body.new)) return { show: true, decide: true };
  if (kind.startsWith('connected_') && status !== 'pending') return { show: false, decide: false };
  if (status === 'pending') return { show: true, decide: true };
  return { show: false, decide: false };
}

export function reviewRowUpdateId(row) {
  const fromMeta = row && row.metadata && row.metadata.update_id;
  if (fromMeta) return String(fromMeta);
  return row && row.id ? String(row.id) : '';
}

function visible(userId, rows) {
  const stones = stonesFor(userId);
  return (rows || []).filter((row) => {
    const id = reviewRowUpdateId(row);
    return !id || !stones.has(id);
  });
}

export function feedFromPayload(payload) {
  const body = payload && typeof payload === 'object' ? payload : {};
  const rawRows = Array.isArray(payload) ? payload : body.notifications;
  const rows = Array.isArray(rawRows) ? rawRows : [];
  return {
    rows,
    serverCount: typeof body.count === 'number' ? body.count : null,
  };
}

export function clearReviewFeedMemory() {
  feeds.clear();
  lists.clear();
  details.clear();
  tombstones.clear();
  inflight.clear();
  clock = 0;
  session = 1;
}

export function peekWebReviews(userId) {
  const slot = feeds.get(scopeKey(userId));
  if (!slot) return null;
  return { ...slot.feed, rows: visible(userId, slot.feed.rows), serverCount: slot.feed.serverCount };
}

export function loadSharedWebReviews(userId, fetcher) {
  const key = scopeKey(userId);
  if (!String(userId || '')) return Promise.resolve(null);
  const existing = inflight.get(`reviews:${key}`);
  if (existing) return existing;
  const started = { userId: String(userId), session, seq: tick() };
  const slot = { promise: Promise.resolve(null) };
  slot.promise = (async () => {
    try {
      const payload = await fetcher();
      if (started.session !== session || scopeKey(started.userId) !== key) return null;
      const feed = feedFromPayload(payload);
      const current = feeds.get(key);
      if (current && current.source === 'server' && current.seq > started.seq) return peekWebReviews(userId);
      const stones = stonesFor(userId);
      const rows = feed.rows.filter((row) => {
        const id = reviewRowUpdateId(row);
        const at = id ? stones.get(id) : undefined;
        if (at == null) return true;
        if (started.seq <= at) return false;
        stones.delete(id);
        return true;
      });
      feeds.set(key, { seq: started.seq, source: 'server', feed: { ...feed, rows } });
      return peekWebReviews(userId);
    } catch {
      return null;
    } finally {
      if (inflight.get(`reviews:${key}`) === slot.promise) inflight.delete(`reviews:${key}`);
    }
  })();
  inflight.set(`reviews:${key}`, slot.promise);
  return slot.promise;
}

export function peekWebUpdates(userId, status) {
  const slot = lists.get(`${scopeKey(userId)}|${status || ''}`);
  if (!slot) return null;
  return slot.rows.filter((row) => {
    const id = row && row.id ? String(row.id) : '';
    return !id || !stonesFor(userId).has(id);
  });
}

export function beginWebListRead() {
  return { session, seq: tick() };
}

export function rememberWebUpdates(userId, status, rows, started) {
  const key = scopeKey(userId);
  if (!String(userId || '') || !Array.isArray(rows) || !started || started.session !== session) {
    return peekWebUpdates(userId, status) || [];
  }
  const listKey = `${key}|${status || ''}`;
  const current = lists.get(listKey);
  if (current && current.source === 'server' && current.seq > started.seq) {
    return peekWebUpdates(userId, status) || [];
  }
  const stones = stonesFor(userId);
  const kept = rows.filter((row) => {
    const id = row && row.id ? String(row.id) : '';
    const at = id ? stones.get(id) : undefined;
    if (at == null) return true;
    if (started.seq <= at) return false;
    stones.delete(id);
    return true;
  });
  lists.set(listKey, { seq: started.seq, source: 'server', rows: kept });
  return peekWebUpdates(userId, status) || [];
}

export function dropResolvedWebItem(userId, id) {
  const key = scopeKey(userId);
  if (!String(userId || '') || !id) return;
  const at = tick();
  stonesFor(userId).set(String(id), at);
  const review = feeds.get(key);
  if (review) {
    feeds.set(key, {
      ...review,
      feed: {
        ...review.feed,
        rows: review.feed.rows.filter((row) => reviewRowUpdateId(row) !== String(id)),
      },
    });
  }
  lists.forEach((slot, listKey) => {
    if (!listKey.startsWith(`${key}|`)) return;
    lists.set(listKey, {
      ...slot,
      rows: slot.rows.filter((row) => String(row && row.id) !== String(id)),
    });
  });
  details.delete(`${key}|${id}`);
}

export function peekWebDetail(userId, updateId) {
  const found = details.get(`${scopeKey(userId)}|${updateId}`);
  if (!found || found.session !== session) return null;
  return found.body;
}

export function rememberWebDetail(owner, updateId, body) {
  if (!owner || owner.session !== session || !owner.userId) return;
  if (!updateId || !body || body.id !== updateId || body.status !== 'pending') return;
  details.set(`${scopeKey(owner.userId)}|${updateId}`, { session: owner.session, body });
}
